"use client";

import { KeySquare } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import * as React from "react";
import { parseEther } from "viem";
import { useAccount, usePublicClient } from "wagmi";
import { ConstantsDriftBanner } from "@/components/features/staking/ConstantsDriftBanner";
import { ModalSkeleton } from "@/components/features/staking/ModalSkeleton";
import { ResponsiveModal } from "@/components/features/staking/ResponsiveModal";
import { Separator } from "@/components/ui";
import { Alert, AlertContent, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { WrapperABI } from "@/contract/abi/wrapper";
import ContractAddress from "@/contract/addresses";
import { useDnmUvmRatio } from "@/hooks/stake/useDnmUvmRatio";
import useFetchStakeContractData from "@/hooks/stake/useFetchStakeContractData";
import { useInvalidateStakeReads } from "@/hooks/stake/useInvalidateStakeReads";
import { usePlanDnmLimits } from "@/hooks/stake/usePlanDnmLimits";
import { useStakeConstants } from "@/hooks/stake/useStakeConstants";
import {
  useApproveDnm,
  useApproveUvm,
  useApproveWrapper,
  useStake,
} from "@/hooks/stake/useStakeWriteCalls";
import { useTxToasts } from "@/hooks/stake/useTxToasts";
import { useUserWrappers } from "@/hooks/stake/useUserWrappers";
import { formatStakeNumber } from "@/lib/stake/format-number";
import { maxDnmForPlan } from "@/lib/stake/plan-dnm-limits";
import { STAKE_CONSTANTS } from "@/lib/stake/stake-constants";
import { DurationChipRow } from "./DurationChipRow";
import { WrapperSelect } from "./WrapperSelect";
import { ZeroWrappersPanel } from "./ZeroWrappersPanel";

type TxState =
  | "idle"
  | "approving-dnm"
  | "approving-uvm"
  | "approving-wrapper"
  | "submitting"
  | "success"
  | "error";

interface StakeModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const MATIC_FLOOR_WEI = 10_000_000_000_000_000n; // 0.01 MATIC advisory floor

export const StakeModal: React.FC<StakeModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const invalidateStakeReads = useInvalidateStakeReads();
  const { showSuccess, showError } = useTxToasts();
  const t = useTranslations("stake");

  const [selectedWrapId, setSelectedWrapId] = React.useState<bigint | null>(
    null,
  );
  const [dnmInput, setDnmInput] = React.useState("");
  const [selectedDuration, setSelectedDuration] = React.useState<bigint | null>(
    null,
  );
  const [txState, setTxState] = React.useState<TxState>("idle");
  const [maticError, setMaticError] = React.useState(false);

  const constants = useStakeConstants();
  const { data: wrappers, isLoading: wrappersLoading } = useUserWrappers();
  const { data: stakeData } = useFetchStakeContractData();
  const { data: planLimits } = usePlanDnmLimits();

  let dnmWei = 0n;
  try {
    dnmWei = dnmInput ? parseEther(dnmInput) : 0n;
  } catch {
    dnmWei = 0n;
  }

  const selectedWrapper = wrappers?.find((w) => w.wrap_id === selectedWrapId);
  const plan = selectedWrapper?.plan_type ?? 1;
  const maxDnmWei = planLimits
    ? maxDnmForPlan(plan, planLimits)
    : STAKE_CONSTANTS.STAKE_MAX_DNM_WEI;
  const uvmRequiredWei = useDnmUvmRatio(dnmWei);

  const dnmValid =
    dnmWei >= STAKE_CONSTANTS.STAKE_MIN_DNM_WEI && dnmWei <= maxDnmWei;

  const maticSufficient =
    stakeData?.user_matic_balance_wei !== undefined
      ? stakeData.user_matic_balance_wei >= MATIC_FLOOR_WEI
      : true;

  const submitEnabled =
    selectedWrapId !== null &&
    dnmValid &&
    selectedDuration !== null &&
    maticSufficient &&
    txState === "idle";

  // Clear matic error when balance becomes sufficient
  React.useEffect(() => {
    if (maticSufficient) setMaticError(false);
  }, [maticSufficient]);

  // Inline slow-connection helper (under submit button). Matches checkout
  // pattern: show after 15s of continuous pending, auto-hide when done.
  const stakeTxPending =
    txState !== "idle" && txState !== "success" && txState !== "error";
  const [showSlowConnection, setShowSlowConnection] = React.useState(false);
  React.useEffect(() => {
    if (!stakeTxPending) {
      setShowSlowConnection(false);
      return;
    }
    const timer = setTimeout(() => setShowSlowConnection(true), 15_000);
    return () => clearTimeout(timer);
  }, [stakeTxPending]);

  // beforeunload guard during tx-pending states (GAP-05 / PM Q9.6)
  React.useEffect(() => {
    if (txState === "idle" || txState === "success") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [txState]);

  // Write hooks — called at render level, not inside onSubmit
  const approveDnm = useApproveDnm(dnmWei);
  const approveUvm = useApproveUvm(uvmRequiredWei);
  const approveWrapper = useApproveWrapper(selectedWrapId ?? 0n);
  const stakeCall = useStake({
    dnmWei,
    wrapId: selectedWrapId ?? 0n,
    durationSec: selectedDuration ?? STAKE_CONSTANTS.ONE_YEAR_SECONDS,
  });

  const handleSubmit = async () => {
    if (!address || !publicClient || !selectedWrapId || !selectedDuration)
      return;

    // GAP-01 Step 0 — MATIC gas precheck
    if (!maticSufficient) {
      setMaticError(true);
      showError(null, t("modal.maticInsufficient"));
      return;
    }

    try {
      setTxState("approving-dnm");

      const allowances = await publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.DNM,
            abi: ERC20_ABI,
            functionName: "allowance",
            args: [address, ContractAddress.STAKE],
          },
          {
            address: ContractAddress.UVM,
            abi: ERC20_ABI,
            functionName: "allowance",
            args: [address, ContractAddress.STAKE],
          },
          {
            address: ContractAddress.WRAPPER,
            abi: WrapperABI,
            functionName: "getApproved",
            args: [selectedWrapId],
          },
        ],
        allowFailure: false,
      });

      const [dnmAllowance, uvmAllowance, wrapperApproved] = allowances as [
        bigint,
        bigint,
        `0x${string}`,
      ];

      if (dnmAllowance < dnmWei) {
        const hash = await approveDnm.execute();
        await publicClient.waitForTransactionReceipt({ hash });
      }

      if (uvmAllowance < uvmRequiredWei) {
        setTxState("approving-uvm");
        const hash = await approveUvm.execute();
        await publicClient.waitForTransactionReceipt({ hash });
      }

      if (
        wrapperApproved.toLowerCase() !== ContractAddress.STAKE.toLowerCase()
      ) {
        setTxState("approving-wrapper");
        const hash = await approveWrapper.execute();
        await publicClient.waitForTransactionReceipt({ hash });
      }

      setTxState("submitting");
      const stakeHash = await stakeCall.execute();
      await publicClient.waitForTransactionReceipt({ hash: stakeHash });

      setTxState("success");
      invalidateStakeReads();
      showSuccess(t("modal.stakeSuccess"));
      setTimeout(() => {
        onOpenChange(false);
        setTxState("idle");
        setDnmInput("");
        setSelectedWrapId(null);
        setSelectedDuration(null);
      }, 300);
    } catch (err) {
      setTxState("error");
      showError(err, t("modal.stakeError"));
      setTimeout(() => setTxState("idle"), 150);
    }
  };

  const renderBody = () => {
    if (constants.isError) {
      return (
        <ConstantsDriftBanner
          driftedKey={constants.error?.message ?? "unknown"}
          expected={0n}
          actual={0n}
        />
      );
    }
    if (constants.isLoading) {
      return <ModalSkeleton rows={4} />;
    }
    // Only show zero-wrappers state AFTER the wrapper fetch settles —
    // while loading, we still render the full modal body and let the
    // WrapperSelect itself show its loading placeholder.
    if (!wrappersLoading && Array.isArray(wrappers) && wrappers.length === 0) {
      return <ZeroWrappersPanel />;
    }
    return (
      <div className="flex flex-col gap-4 py-2">
        {/* Wrapper select */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="stake-wrapper-select"
            className="text-xs text-text-secondary"
          >
            {t("modal.wrapperSelectLabel")}
          </label>
          <WrapperSelect
            wrappers={wrappers ?? []}
            selectedWrapId={selectedWrapId}
            onSelect={setSelectedWrapId}
            isLoading={wrappersLoading}
          />
        </div>

        {/* DNM input */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="stake-dnm-input"
            className="text-xs text-text-secondary"
          >
            {t("modal.dnmInputLabel")}
          </label>
          <Input
            id="stake-dnm-input"
            type="number"
            dir="ltr"
            value={dnmInput}
            onChange={(e) => setDnmInput(e.target.value)}
            placeholder="0"
            className="h-10 bg-transparent text-end"
          />
          <span className="text-[10px] text-text-disabled">
            {t("modal.dnmMaxHint", { max: formatStakeNumber(maxDnmWei) })}
          </span>
        </div>

        {/* Duration chips */}
        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary">
            {t("modal.durationLabel")}
          </label>
          <DurationChipRow
            selectedDurationSec={selectedDuration}
            onChange={setSelectedDuration}
          />
        </div>

        {/* UVM calculated output (read-only field per Figma) */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="stake-uvm-output"
            className="text-xs text-text-secondary"
          >
            {t("modal.uvmOutputLabel")}
          </label>
          <Input
            id="stake-uvm-output"
            type="text"
            dir="ltr"
            readOnly
            value={uvmRequiredWei > 0n ? formatStakeNumber(uvmRequiredWei) : ""}
            placeholder="0"
            className="h-10 bg-transparent text-end"
          />
        </div>

        {/* MATIC insufficient alert */}
        {(!maticSufficient || maticError) && (
          <Alert variant="destructive" className="w-full">
            <AlertContent>
              <AlertDescription className="text-xs">
                {t("modal.maticInsufficient")}
              </AlertDescription>
            </AlertContent>
          </Alert>
        )}

        <Separator className="bg-stroke-subtle" />
        <div className="mt-2 flex flex-col gap-2">
          <Button
            type="button"
            variant={submitEnabled ? "solid" : "flat"}
            colors="secondary"
            disabled={!submitEnabled}
            loading={stakeTxPending}
            onClick={handleSubmit}
            startIcon={<KeySquare size={18} />}
            className="self-end"
          >
            {t("modal.submitButton")}
          </Button>
          {showSlowConnection && (
            <div
              className="self-center flex items-center gap-2 text-xs text-text-secondary mt-2"
              data-testid="slow-connection-warning"
              role="status"
              aria-live="polite"
            >
              <span
                className="size-3 shrink-0 animate-spin rounded-full border-2 border-stroke-light border-t-text-secondary"
                aria-hidden
              />
              <span>{t("modal.slowConnectionNote")}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("modal.title")}
      desktopMaxWidth="lg"
    >
      {renderBody()}
    </ResponsiveModal>
  );
};
